

import axiosInstance from './axiosInstance';

export type Vendor = {
  vendorOrgXref?: any;
  _id?: string;
  vendorId?: string;
  name: string;
  contactEmail: string;
  contactNumber: string;
  organization?: string;
  status: string;
  actions?: string;
  gsTin: string;
  logo?: string;
  logoUrl?: string;
  logoFile?: File;
  attachments?: (File | string)[];
  address: {
    id?: string;
    locationName: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
};

export type Product = {
  vendorProductId: string;
  productId: string;
  vendorName: string;
  productName: string;
  productType: string;
  productDescription: string;
  productStatus: string;
  location: string;
  installedDate: string;
  hasAMC: boolean;
  productImage?: string;
  maintenanceSchedule: {
    scheduleId: string;
    productId: string;
    scheduledDate: string;
    maintenanceDetails: string;
    status: string;
  };
};

export interface VendorProductFlat {
  id(productId: string, id: any): void;
  vendorProductId: string;
  vendorId: string;
  vendorName: string;
  productId: string;
  productName: string;
  productType: string;
  productDescription: string;
  calibrationFrequency: string | null;
  productImage?: string;
  productStatus: string;
  location: string;
  installedDate: string;
  hasAMC: boolean;
  isDeleted: boolean;
  type: string;
  status: string;
  maintenanceSchedule: {
    scheduleId: string | null;
    productId: string | null;
    scheduledDate: string;
    maintenanceDetails: string;
    status: string;
  };
}

export type VendorName = {
  vendorId: string;
  name: string;
};

export type VendorOrgPayload = {
  vendorId: string;
  organisationId: string;
  relationshipType?: string;
  isActive?: boolean;
};

//****** VENDOR API CALLS ******

export const vendorRepository = {

  // Get all vendors
  getVendors: async (): Promise<Vendor[]> => {
    const response = await axiosInstance.get('/api/vendors');

    const vendors = response.data?.data || [];

    return vendors.map((v: any) => ({
      vendorId: v.vendorId,
      name: v.name,
    }));
  },

  // Get vendor by ID
  getVendorById: async (id: string): Promise<Vendor> => {
    const { data } = await axiosInstance.get(`/api/vendors/${id}`);
    return data;
  },

  // Delete vendor
  deleteVendor: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/vendors/${id}`);
  },

  // PUT: Update vendor
  updateVendor: async (vendorId: string, data: any): Promise<void> => {
    await axiosInstance.put(`/api/vendors/${vendorId}`, data);
  },

  patchVendor: async (vendorId: string, payload: Vendor): Promise<Vendor> => {
    const formData = new FormData();

    // Basic fields
    formData.append("Name", payload.name);
    formData.append("ContactEmail", payload.contactEmail);
    formData.append("ContactNumber", payload.contactNumber);
    formData.append("GsTin", payload.gsTin);
    formData.append("Status", payload.status);

    if (payload.organization) {
      formData.append("OrgId", payload.organization);
    }

    // Address fields
    formData.append("AddressLocationName", payload.address.locationName || "");
    formData.append("Street", payload.address.street || "");
    formData.append("City", payload.address.city || "");
    formData.append("State", payload.address.state || "");
    formData.append("ZipCode", payload.address.zipCode || "");
    formData.append("Country", payload.address.country || "");

    // Logo
    if (payload.logoFile) {
      formData.append("LogoFile", payload.logoFile);
    }

    // Attachments
    if (payload.attachments && payload.attachments.length > 0) {
      payload.attachments.forEach((file) => {
        formData.append("Attachments", file);
      });
    }

    const { data } = await axiosInstance.patch(`/api/vendors/${vendorId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data;
  },

  // export vendorlist
  exportVendors: async (
    city?: string,
    status?: string,
    search?: string,
    format: "csv" | "xlsx" = "csv"
  ): Promise<Blob> => {
    const response = await axiosInstance.get('/api/vendors/export', {
      params: {
        format,
        city,
        status,
        search,
      },
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    });

    return response.data;
  },

  //  UPDATED: Add new vendor (Logo + Attachments in same API)
  addVendor: async (payload: Vendor): Promise<Vendor> => {
    const formData = new FormData();

    // Basic fields
    formData.append("Name", payload.name);
    formData.append("ContactEmail", payload.contactEmail);
    formData.append("ContactNumber", payload.contactNumber);
    formData.append("GsTin", payload.gsTin);
    formData.append("Status", payload.status);

    if (payload.organization) {
      formData.append("OrgId", payload.organization);
    }

    // Address fields
    formData.append("AddressLocationName", payload.address.locationName || "");
    formData.append("Street", payload.address.street || "");
    formData.append("City", payload.address.city || "");
    formData.append("State", payload.address.state || "");
    formData.append("ZipCode", payload.address.zipCode || "");
    formData.append("Country", payload.address.country || "");

    // ✅ Logo
    if (payload.logoFile) {
      formData.append("LogoFile", payload.logoFile);
    }

    // ✅ Attachments
    if (payload.attachments && payload.attachments.length > 0) {
      payload.attachments.forEach((file) => {
        formData.append("Attachments", file);
      });
    }

    const { data } = await axiosInstance.post('/api/vendors', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data;
  },

  // Get vendor names (id + name only)
  getVendorNames: async (): Promise<VendorName[]> => {
    const { data } = await axiosInstance.get('/api/vendors/names');
    return data;
  },

  // Assign vendor to org
  assignVendorToOrg: async (
    payload: VendorOrgPayload & { relationshipType?: string; isActive?: boolean }
  ): Promise<Vendor> => {
    const { data } = await axiosInstance.post(
      '/api/vendors/vendor-org-xref',
      {
        ...payload,
        relationshipType: payload.relationshipType || 'Primary',
        isActive: payload.isActive ?? true,
      }
    );
    return data;
  },

  // Get all vendors
  getAllVendors: async (): Promise<Vendor[]> => {
    const { data } = await axiosInstance.get('/api/vendors/all');
    return data;
  },

  // Get unassigned vendors
  getUnassignedVendors: async (): Promise<Vendor[]> => {
    const { data } = await axiosInstance.get('/api/vendors/unassigned');
    return data;
  },

  // Bulk assign vendors
  assignVendorsToOrgBulk: async (vendorIds: string[]) => {
    const { data } = await axiosInstance.post(
      '/api/vendors/vendor-org-xref/bulk',
      vendorIds
    );
    return data;
  },

  //****** VENDOR PRODUCTS API CALLS ******

  getVendorProducts: async (): Promise<VendorProductFlat[]> => {
    const { data } = await axiosInstance.get(
      "/api/VendorProducts/flat-products"
    );
    return data;
  },

  getVendorProductById: async (
    vendorProductId: string,
    productId: string
  ): Promise<Product> => {
    const { data } = await axiosInstance.get(
      `/api/VendorProducts/${vendorProductId}/product/${productId}`
    );
    return data;
  },

  deleteVendorProduct: async (
    vendorProductId: string,
    productId: string
  ): Promise<void> => {
    await axiosInstance.delete(
      `/api/VendorProducts/${vendorProductId}/product/${productId}`
    );
  },

  addVendorProduct: async (payload: any) => {
    const response = await axiosInstance.post(`/api/VendorProducts`, payload);
    return response.data;
  },

  updateVendorProduct: async (
    vendorProductId: string,
    productId: string,
    product: any,
    file?: File
  ) => {
    const formData = new FormData();

    if (product.productName)
      formData.append("ProductName", product.productName);

    if (product.productType)
      formData.append("ProductType", product.productType);

    if (product.productDescription)
      formData.append("ProductDescription", product.productDescription);

    if (product.productStatus)
      formData.append("ProductStatus", product.productStatus);

    if (product.location)
      formData.append("Location", product.location);

    if (product.installedDate)
      formData.append(
        "InstalledDate",
        new Date(product.installedDate).toISOString()
      );

    if (product.hasAMC !== undefined)
      formData.append("HasAMC", product.hasAMC.toString());

    if (product.calibrationFrequency)
      formData.append("CalibrationFrequency", product.calibrationFrequency);

    if (product.maintenanceSchedule)
      formData.append(
        "MaintenanceSchedule",
        JSON.stringify(product.maintenanceSchedule)
      );

    if (file) {
      formData.append("file", file);
    }

    const { data } = await axiosInstance.patch(
      `/api/VendorProducts/${vendorProductId}/product/${productId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return {
      ...data,
      imageUrl: data.fileUrl || data.imageUrl
    };
  },

  getVendorsPaginated: async (
    pageNumber: number,
    pageSize: number,
    city: string,
    status: string,
    search: string,
    appliedSearch?: string
  ) => {
    const { data } = await axiosInstance.get("/api/vendors", {
      params: {
        pageNumber,
        pageSize,
        city,
        status,
        search,
        appliedSearch,
      }
    });
    return data;
  },

  getVendorProductsPaginated: async (
    pageNumber: number,
    pageSize: number,
    vendorName: string,
    type: string,
    search: string
  ) => {
    const { data } = await axiosInstance.get("/api/VendorProducts", {
      params: {
        pageNumber,
        pageSize,
        search,
        type,
        vendorName,
      },
    });

    return data;
  },

  getvendorProductbyVendor: async (id: string) => {
    const { data } = await axiosInstance.get(
      `/api/VendorProducts/vendor/${id}`
    );
    return data;
  },

  uploadProductImage: async (
    vendorProductId: string,
    productId: string,
    file: File
  ) => {
    const formData = new FormData();
    formData.append("files", file);

    const { data } = await axiosInstance.post(
      `/api/VendorProducts/${vendorProductId}/products/${productId}/upload-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return data.url;
  },
  // Download vendor attachment (by fileKey)
  downloadVendorFile: async (fileKey: string): Promise<Blob> => {
    const response = await axiosInstance.get('/api/vendors/download', {
      params: { fileKey },
      responseType: 'blob',
    });

    return response.data;
  },
  downloadProductImage: async (fileUrl: string) => {
    const res = await axiosInstance.get(
      `/api/VendorProducts/download-product-image`,
      {
        params: { fileUrl },
        responseType: "blob",
      }
    );
    return res.data;
  },

  exportVendorProducts: async (
    vendorName?: string,
    type?: string,
    status?: string,
    search?: string,
    format: "csv" | "xlsx" = "csv"
  ): Promise<Blob> => {
    const response = await axiosInstance.get(
      "/api/VendorProducts/export",
      {
        params: {
          format,
          vendorName,
          type,
          status,
          search,
        },
        responseType: "blob",
      }
    );

    return response.data;
  },

  getProductTypes: async (): Promise<{ id: string; type: string }[]> => {
    const { data } = await axiosInstance.get("/api/ProductTypes");
    return data;
  },
};